import { Box, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type {
  FilterValue,
  StringFilterValue,
  MediaFilterValue,
} from '@/features/filters';
import {
  isDimensionFilter,
  isMediaFilter,
  isStringFilter,
  useFiltersParamsContext,
  StringFilter,
  MediaFilter,
  useFiltersContext,
} from '@/features/filters';
import { isLoading } from '@/shared/lib';
import { Button as SharedButton, Loader } from '@/shared/ui';

const Button = styled(SharedButton)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  lineHeight: '1.125rem',
  borderRadius: theme.shape.borderRadius * 2,
}));

interface Props {
  id: string;
  onClose: () => void;
}

export function AddFilter({ id, onClose }: Props) {
  const { filtersMap, updateFilters, submitFilters, resetToInitialFilters } =
    useFiltersContext();
  const { paramsData, dimensionConfigs } = useFiltersParamsContext();
  const filterConfig = useMemo(
    () => dimensionConfigs.find((config) => config.id === id),
    [dimensionConfigs, id],
  );

  if (!filterConfig) {
    return null;
  }

  if (!isDimensionFilter(filterConfig.type)) {
    return <p>Unknown filter type: {filterConfig.type}</p>;
  }

  const loading =
    !paramsData[filterConfig.id] ||
    isLoading(paramsData[filterConfig.id].status);

  const handleChange = (id: string) => (value: FilterValue) => {
    updateFilters(id, value);
  };

  let content: ReactNode = null;

  if (isStringFilter(filterConfig.type)) {
    content = (
      <StringFilter
        id={id}
        onChange={handleChange(id)}
        value={filtersMap[id] as StringFilterValue}
      />
    );
  } else if (isMediaFilter(filterConfig.type)) {
    content = (
      <MediaFilter
        id={id}
        onChange={handleChange(id)}
        value={filtersMap[id] as MediaFilterValue}
      />
    );
  }

  const handleApply = () => {
    submitFilters();
    onClose();
  };
  const handleCancel = () => {
    resetToInitialFilters();
    onClose();
  };

  return (
    <Box sx={{ width: 304 }}>
      {content}
      <Divider />
      <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button onClick={handleApply}>Apply</Button>
        <Button color="secondary" variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
      </Box>
      <Loader active={loading} />
    </Box>
  );
}
